var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { inject } from './../decorators/inject';
import { handleDecorator } from './../decorators/handleDecorator';
import { diffProperty } from './../decorators/diffProperty';
import { ThemeInjector, isThemeWithVariant, isThemeWithVariants, isThemeInjectorPayloadWithVariant } from '../ThemeInjector';
export const THEME_KEY = ' _key';
export const INJECTED_THEME_KEY = '__theme_injector';
/**
 * Decorator for base css classes
 */
export function theme(theme) {
    return handleDecorator((target) => {
        target.addDecorator('baseThemeClasses', theme);
    });
}
/**
 * Creates a reverse lookup for the classes passed in via the `theme` function.
 *
 * @param classes The baseClasses object
 * @requires
 */
function createThemeClassesLookup(classes) {
    return classes.reduce((currentClassNames, baseClass) => {
        Object.keys(baseClass).forEach((key) => {
            currentClassNames[baseClass[key]] = key;
        });
        return currentClassNames;
    }, {});
}
/**
 * Convenience function that is given a theme and an optional registry, the theme
 * injector is defined against the registry, returning the theme.
 *
 * @param theme the theme to set
 * @param themeRegistry registry to define the theme injector against. Defaults
 * to the global registry
 *
 * @returns the theme injector used to set the theme
 */
export function registerThemeInjector(theme, themeRegistry) {
    const themeInjector = new ThemeInjector(theme);
    themeRegistry.defineInjector(INJECTED_THEME_KEY, (invalidator) => {
        themeInjector.setInvalidator(invalidator);
        return () => themeInjector;
    });
    return themeInjector;
}
/**
 * Function that returns a class decorated with with Themed functionality
 */
export function ThemedMixin(Base) {
    let Themed = class Themed extends Base {
        constructor() {
            super(...arguments);
            /**
             * Registered base theme keys
             */
            this._registeredBaseThemeKeys = [];
            /**
             * Indicates if classes meta data need to be calculated.
             */
            this._recalculateClasses = true;
            /**
             * Loaded theme
             */
            this._theme = {};
        }
        theme(classes) {
            if (this._recalculateClasses) {
                this._recalculateThemeClasses();
            }
            if (Array.isArray(classes)) {
                return classes.map((className) => this._getThemeClass(className));
            }
            return this._getThemeClass(classes);
        }
        variant() {
            const { theme } = this.properties;
            if (theme && isThemeWithVariant(theme)) {
                return theme.variant.value.root;
            }
        }
        /**
         * Function fired when `theme` or `extraClasses` are changed.
         */
        onPropertiesChanged() {
            this._recalculateClasses = true;
        }
        _getThemeClass(className) {
            if (className === undefined || className === null || className === false || className === true) {
                return className;
            }
            const extraClasses = this.properties.extraClasses || {};
            const themeClassName = this._baseThemeClassesReverseLookup[className];
            let resultClassNames = [];
            if (!themeClassName) {
                console.warn(`Class name: '${className}' not found in theme`);
                return null;
            }
            if (this._classes) {
                const classes = Object.keys(this._classes).reduce((classes, key) => {
                    const classNames = Object.keys(this._classes[key]);
                    for (let i = 0; i < classNames.length; i++) {
                        const extraClass = this._classes[key][classNames[i]];
                        if (classNames[i] === themeClassName && extraClass) {
                            extraClass.forEach((className) => {
                                if (className && className !== true) {
                                    classes.push(className);
                                }
                            });
                            break;
                        }
                    }
                    return classes;
                }, []);
                resultClassNames.push(...classes);
            }
            if (extraClasses[themeClassName]) {
                resultClassNames.push(extraClasses[themeClassName]);
            }
            if (this._theme[themeClassName]) {
                resultClassNames.push(this._theme[themeClassName]);
            }
            else {
                resultClassNames.push(this._registeredBaseTheme[themeClassName]);
            }
            return resultClassNames.join(' ');
        }
        _recalculateThemeClasses() {
            let { theme: themeProp = {}, classes = {} } = this.properties;
            let theme;
            if (isThemeWithVariant(themeProp)) {
                theme = isThemeWithVariants(themeProp.theme) ? themeProp.theme.theme : themeProp.theme;
            }
            else {
                theme = themeProp;
            }
            if (!this._registeredBaseTheme) {
                const baseThemes = this.getDecorator('baseThemeClasses');
                if (baseThemes.length === 0) {
                    console.warn('A base theme has not been provided to this widget. Please use the @theme decorator to add a theme.');
                }
                this._registeredBaseTheme = baseThemes.reduce((finalBaseTheme, baseTheme) => {
                    const _a = THEME_KEY, key = baseTheme[_a], classes = __rest(baseTheme, [typeof _a === "symbol" ? _a : _a + ""]);
                    this._registeredBaseThemeKeys.push(key);
                    return Object.assign({}, finalBaseTheme, classes);
                }, {});
                this._baseThemeClassesReverseLookup = createThemeClassesLookup(baseThemes);
            }
            this._theme = this._registeredBaseThemeKeys.reduce((baseTheme, themeKey) => {
                return Object.assign({}, baseTheme, theme[themeKey]);
            }, {});
            this._classes = Object.keys(classes).reduce((computed, key) => {
                if (this._registeredBaseThemeKeys.indexOf(key) > -1) {
                    computed = Object.assign({}, computed, { [key]: classes[key] });
                }
                return computed;
            }, {});
            this._recalculateClasses = false;
        }
    };
    __decorate([
        diffProperty('theme'),
        diffProperty('extraClasses'),
        diffProperty('classes')
    ], Themed.prototype, "onPropertiesChanged", null);
    Themed = __decorate([
        inject({
            name: INJECTED_THEME_KEY,
            getProperties: (theme, properties) => {
                if (!properties.theme) {
                    const payload = theme.get();
                    if (isThemeInjectorPayloadWithVariant(payload)) {
                        return { theme: payload };
                    }
                    else if (payload) {
                        return { theme: payload.theme };
                    }
                }
                return {};
            }
        })
    ], Themed);
    return Themed;
}
export default ThemedMixin;
//# sourceMappingURL=Themed.mjs.map